import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Skeleton,
} from '@mui/material'

const MobileSkeleton = ({ animation }) => (
  <Grid container spacing={2}>
    {Array.from(Array(4)).map((_, index) => (
      <Grid
        key={index}
        size={{
          xs: 12,
          sm: 6,
          md: 6,
          lg: 4,
          xl: 3,
        }}
      >
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title={<Skeleton variant="rectangular" height={40} animation={animation} />}
            subheader={<Skeleton variant="text" animation={animation} />}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Skeleton variant="rectangular" height={200} animation={animation} />
          </CardContent>
          <CardActions disableSpacing>
            <Skeleton
              variant="rectangular"
              sx={{ width: 25, height: 25, marginLeft: 'auto' }}
              animation={animation}
            />
          </CardActions>
        </Card>
      </Grid>
    ))}
  </Grid>
)

const SidebarChipGroup = ({ titleWidth, items, animation }) => (
  <>
    <Skeleton variant="text" width={titleWidth} height={24} sx={{ mb: 1 }} animation={animation} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Array.from(Array(items)).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={32}
          sx={{ borderRadius: 16 }}
          animation={animation}
        />
      ))}
    </Box>
  </>
)

const TableRowSkeleton = ({ animation }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ width: 50, display: 'flex', justifyContent: 'center' }}>
      <Skeleton variant="rectangular" width={20} height={20} animation={animation} />
    </Box>
    <Skeleton
      variant="rounded"
      height={24}
      sx={{ width: 90, borderRadius: 12 }}
      animation={animation}
    />
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Skeleton variant="circular" width={36} height={36} animation={animation} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="30%" height={14} animation={animation} />
        <Skeleton variant="text" width="65%" height={20} animation={animation} />
      </Box>
    </Box>
    <Skeleton variant="text" sx={{ width: 50 }} animation={animation} />
    <Skeleton variant="text" sx={{ width: 90 }} animation={animation} />
    <Box sx={{ width: 250, display: 'flex', gap: 0.5, justifyContent: 'flex-start' }}>
      {Array.from(Array(5)).map((_, j) => (
        <Skeleton key={j} variant="circular" width={32} height={32} animation={animation} />
      ))}
    </Box>
  </Box>
)

const DesktopSkeleton = ({ animation }) => (
  <Box>
    {/* Top bar: search + info alert + view toggle */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 2 }}>
      <Skeleton
        variant="rounded"
        height={56}
        sx={{ flex: 1, maxWidth: { md: '40%', lg: '35%' } }}
        animation={animation}
      />
      <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} animation={animation} />
      <Box sx={{ display: 'flex', ml: 'auto', gap: 0 }}>
        <Skeleton
          variant="rounded"
          width={56}
          height={40}
          sx={{ borderRadius: '4px 0 0 4px' }}
          animation={animation}
        />
        <Skeleton
          variant="rounded"
          width={56}
          height={40}
          sx={{ borderRadius: '0 4px 4px 0' }}
          animation={animation}
        />
      </Box>
    </Box>

    {/* Sidebar + content */}
    <Box sx={{ display: 'flex', mt: 1 }}>
      <Box sx={{ width: 200, flexShrink: 0 }}>
        <SidebarChipGroup titleWidth={60} items={3} animation={animation} />
        <Box sx={{ mt: 2 }}>
          <SidebarChipGroup titleWidth={120} items={4} animation={animation} />
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ width: 50 }} />
          <Skeleton variant="text" width={60} height={20} animation={animation} />
          <Skeleton
            variant="text"
            sx={{ flex: 1, maxWidth: 120 }}
            height={20}
            animation={animation}
          />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="text" width={50} height={20} animation={animation} />
          <Skeleton variant="text" width={90} height={20} animation={animation} />
          <Box sx={{ width: 250 }}>
            <Skeleton variant="text" width={70} height={20} animation={animation} />
          </Box>
        </Box>

        {Array.from(Array(8)).map((_, i) => (
          <TableRowSkeleton key={i} animation={animation} />
        ))}
      </Box>
    </Box>
  </Box>
)

const DocumentSkeleton = ({ animation = true }) => (
  <>
    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      <MobileSkeleton animation={animation} />
    </Box>
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <DesktopSkeleton animation={animation} />
    </Box>
  </>
)

export default DocumentSkeleton
